import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { 
  Mail, Send, CheckCircle, XCircle, AlertTriangle, Clock, 
  Shield, User, FileText, Eye, Upload, Trash2, ArrowLeft, CreditCard
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

// ID Verification Component
// Fields: Name, DOB, Father Name, PAN Number, PAN Copy
// Send mail to vendor with PAN copy, mark as verified correct, Agent Remarks

export default function IDVerificationForm({ 
  caseId, 
  checkId, 
  checkData, 
  employeeData,
  documents = [],
  onBack,
  onComplete 
}) {
  const [loading, setLoading] = useState(false);
  const [showSendToVendor, setShowSendToVendor] = useState(false);
  const [showRecordResponse, setShowRecordResponse] = useState(false);
  const [verificationData, setVerificationData] = useState(checkData?.id_verification || {});
  const [vendors, setVendors] = useState([]);
  
  // Annexure state
  const [annexures, setAnnexures] = useState([]);
  const [showUploadAnnexure, setShowUploadAnnexure] = useState(false);
  const [uploadingAnnexure, setUploadingAnnexure] = useState(false);
  const [annexureForm, setAnnexureForm] = useState({
    file_name: '',
    file_url: '',
    file_type: 'document',
    description: '',
    annexure_type: 'pan_card'
  });
  
  // Vendor form state
  const [vendorForm, setVendorForm] = useState({
    vendor_id: '',
    vendor_name: '',
    vendor_email: '',
    include_pan_copy: true,
    additional_instructions: ''
  });
  
  // Response form state
  const [responseForm, setResponseForm] = useState({
    verifier_name: '',
    verification_date: '',
    pan_verified: 'yes',
    pan_status: 'active',
    name_on_pan: '',
    pan_match_status: 'matched',
    agent_remarks: '',
    overall_status: 'verified',
    overall_remarks: ''
  });

  useEffect(() => {
    fetchData();
  }, [caseId, checkId]);

  const fetchData = async () => {
    try {
      const [annexuresRes, vendorsRes] = await Promise.all([
        axios.get(`/cases/${caseId}/checks/${checkId}/annexures`),
        axios.get('/vendors').catch(() => ({ data: [] }))
      ]);
      setAnnexures(annexuresRes.data || []);
      setVendors(vendorsRes.data || []);
    } catch (e) {
      console.error('Failed to fetch data');
    }
  };

  const handleUploadAnnexure = async () => {
    if (!annexureForm.file_url) {
      toast.error('Please provide file URL');
      return;
    }
    
    setUploadingAnnexure(true);
    try {
      const res = await axios.post(`/cases/${caseId}/checks/${checkId}/annexures`, {
        case_id: caseId,
        check_id: checkId,
        ...annexureForm
      });
      setAnnexures(prev => [...prev, res.data]);
      setShowUploadAnnexure(false);
      setAnnexureForm({
        file_name: '',
        file_url: '',
        file_type: 'document',
        description: '',
        annexure_type: 'pan_card'
      });
      toast.success('Annexure uploaded');
    } catch (error) {
      toast.error('Failed to upload');
    } finally {
      setUploadingAnnexure(false);
    }
  };

  const handleDeleteAnnexure = async (annexureId) => {
    if (!confirm('Delete this annexure?')) return;
    try {
      await axios.delete(`/annexures/${annexureId}`);
      setAnnexures(prev => prev.filter(a => a.id !== annexureId));
      toast.success('Deleted');
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleSendToVendor = async () => {
    if (!vendorForm.vendor_email) {
      toast.error('Please enter vendor email');
      return;
    }
    
    setLoading(true);
    try {
      const panAnnexure = annexures.find(a => a.annexure_type === 'pan_card');
      
      await axios.post(`/cases/${caseId}/checks/${checkId}/send-id-verification`, {
        ...vendorForm,
        employee_name: employeeData?.name,
        dob: employeeData?.dob,
        father_name: employeeData?.father_name,
        pan_number: employeeData?.pan_number,
        pan_copy_url: vendorForm.include_pan_copy ? panAnnexure?.file_url : null
      });
      toast.success('Verification request sent to vendor (MOCKED)');
      setVerificationData(prev => ({
        ...prev,
        initiated_at: new Date().toISOString(),
        vendor_assigned: true,
        vendor_name: vendorForm.vendor_name
      }));
      setShowSendToVendor(false);
    } catch (error) {
      toast.error('Failed to send');
    } finally {
      setLoading(false);
    }
  };

  const handleRecordResponse = async () => {
    if (!responseForm.verifier_name) {
      toast.error('Please enter verifier name');
      return;
    }
    
    setLoading(true);
    try {
      const res = await axios.put(`/cases/${caseId}/checks/${checkId}/record-id-verification`, {
        ...responseForm,
        check_type: 'id_check'
      });
      toast.success(`Verification recorded - Status: ${responseForm.overall_status.toUpperCase()}`);
      setVerificationData(prev => ({ ...prev, ...res.data }));
      setShowRecordResponse(false);
      if (onComplete) onComplete(res.data);
    } catch (error) {
      toast.error('Failed to record');
    } finally {
      setLoading(false);
    }
  };

  const getResultBadge = (result) => {
    switch(result) {
      case 'verified': return <Badge className="bg-emerald-100 text-emerald-700">VERIFIED</Badge>;
      case 'not_verified': return <Badge className="bg-amber-100 text-amber-700">NOT VERIFIED</Badge>;
      case 'discrepancy': return <Badge className="bg-rose-100 text-rose-700">DISCREPANCY</Badge>;
      default: return <Badge variant="outline">Pending</Badge>;
    }
  };

  const panDocument = documents.find(d => d.document_type === 'pan_card' || d.document_type === 'pan');
  const panAnnexure = annexures.find(a => a.annexure_type === 'pan_card');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" onClick={onBack}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Shield className="w-6 h-6 text-blue-600" />
              ID Verification (PAN)
            </h2>
            <p className="text-slate-600">Case ID: {caseId} | Check ID: {checkId}</p>
          </div>
        </div>
        {getResultBadge(verificationData?.overall_status)}
      </div>

      {/* Candidate Info */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="w-5 h-5 text-blue-600" />
            Candidate Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <Label className="text-slate-500">Name</Label>
              <p className="font-medium">{employeeData?.name || 'N/A'}</p>
            </div>
            <div>
              <Label className="text-slate-500">Date of Birth</Label>
              <p className="font-medium">{employeeData?.dob || 'N/A'}</p>
            </div>
            <div>
              <Label className="text-slate-500">Father&apos;s Name</Label>
              <p className="font-medium">{employeeData?.father_name || 'N/A'}</p>
            </div>
            <div className="bg-blue-50 p-2 rounded-lg border border-blue-200">
              <Label className="text-blue-600">PAN Number</Label>
              <p className="font-bold text-blue-900">{employeeData?.pan_number || 'N/A'}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* PAN Document */}
      <Card className="border-blue-200">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-blue-600" />
            PAN Card Document
          </CardTitle>
          <Button onClick={() => setShowUploadAnnexure(true)} size="sm" className="bg-blue-600 hover:bg-blue-700">
            <Upload className="w-4 h-4 mr-2" />
            Upload PAN Copy
          </Button>
        </CardHeader>
        <CardContent>
          {(panDocument || panAnnexure) ? (
            <div className="flex items-center gap-4">
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 flex-1">
                <p className="font-medium">PAN Card Copy Available</p>
                <p className="text-sm text-blue-600">
                  {panAnnexure?.file_name || panDocument?.file_name || 'PAN Document'}
                </p>
              </div>
              <Button 
                variant="outline"
                onClick={() => window.open(panAnnexure?.file_url || panDocument?.file_url, '_blank')}
              >
                <Eye className="w-4 h-4 mr-2" />
                View
              </Button>
            </div>
          ) : (
            <p className="text-slate-500 text-center py-4">
              No PAN card document uploaded. Please upload a copy of PAN card.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Annexures */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Verification Annexures
          </CardTitle>
        </CardHeader>
        <CardContent>
          {annexures.length === 0 ? (
            <p className="text-slate-500 text-center py-4">No annexures uploaded</p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {annexures.map((annexure, idx) => (
                <div key={idx} className="border rounded-lg p-3 bg-slate-50 relative group">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 text-rose-600 h-6 w-6 p-0"
                    onClick={() => handleDeleteAnnexure(annexure.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                  <Badge variant="outline" className="text-xs mb-2">{annexure.annexure_type}</Badge>
                  <p className="text-sm font-medium truncate">{annexure.file_name || 'Document'}</p>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="w-full mt-2 text-xs h-7"
                    onClick={() => window.open(annexure.file_url, '_blank')}
                  >
                    <Eye className="w-3 h-3 mr-1" />
                    View
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-4">
        <Button onClick={() => setShowSendToVendor(true)} className="bg-blue-600 hover:bg-blue-700">
          <Mail className="w-4 h-4 mr-2" />
          Send to Vendor
        </Button>
        <Button onClick={() => setShowRecordResponse(true)} className="bg-emerald-600 hover:bg-emerald-700">
          <CheckCircle className="w-4 h-4 mr-2" />
          Record Verification
        </Button>
      </div>

      {/* Verification Result */}
      {verificationData?.overall_status && verificationData.overall_status !== 'pending' && (
        <Card className={
          verificationData.overall_status === 'verified' ? 'border-emerald-200 bg-emerald-50' :
          verificationData.overall_status === 'discrepancy' ? 'border-rose-200 bg-rose-50' :
          'border-amber-200 bg-amber-50'
        }>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Verification Result</span>
              {getResultBadge(verificationData.overall_status)}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <Label className="text-slate-500">Verified By</Label>
                <p className="font-medium">{verificationData.verifier_name || 'N/A'}</p>
              </div>
              <div>
                <Label className="text-slate-500">PAN Status</Label>
                <p className="font-medium capitalize">{verificationData.pan_status || 'N/A'}</p>
              </div>
              <div>
                <Label className="text-slate-500">Name on PAN</Label>
                <p className="font-medium">{verificationData.name_on_pan || 'N/A'}</p>
              </div>
              <div>
                <Label className="text-slate-500">Match Status</Label>
                <p className="font-medium capitalize">{verificationData.pan_match_status || 'N/A'}</p>
              </div>
            </div>
            {verificationData.agent_remarks && (
              <div className="mt-4 p-3 bg-blue-100 rounded-lg">
                <Label className="text-blue-800">Agent Remarks</Label>
                <p className="text-blue-900">{verificationData.agent_remarks}</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Send to Vendor Dialog */}
      <Dialog open={showSendToVendor} onOpenChange={setShowSendToVendor}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5" />
              Send ID Verification to Vendor
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="font-medium text-blue-800">Email will include:</p>
              <ul className="text-sm text-blue-700 list-disc list-inside mt-1">
                <li>Candidate Name: {employeeData?.name}</li>
                <li>DOB: {employeeData?.dob}</li>
                <li>Father&apos;s Name: {employeeData?.father_name}</li>
                <li>PAN Number: {employeeData?.pan_number}</li>
              </ul>
            </div>

            <div className="flex items-center gap-2">
              <input 
                type="checkbox"
                id="include-pan"
                checked={vendorForm.include_pan_copy}
                onChange={(e) => setVendorForm(prev => ({ ...prev, include_pan_copy: e.target.checked }))}
              />
              <Label htmlFor="include-pan" className="cursor-pointer">
                Include PAN Card Copy in Email
              </Label>
            </div>

            {vendors.length > 0 && (
              <div>
                <Label>Select Vendor</Label>
                <Select 
                  value={vendorForm.vendor_id}
                  onValueChange={(v) => {
                    const vendor = vendors.find(vd => vd.id === v);
                    setVendorForm(prev => ({
                      ...prev,
                      vendor_id: v,
                      vendor_name: vendor?.name || '',
                      vendor_email: vendor?.email || ''
                    }));
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select vendor" />
                  </SelectTrigger>
                  <SelectContent>
                    {vendors.map((v) => (
                      <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <Label>Vendor Name *</Label>
              <Input 
                value={vendorForm.vendor_name}
                onChange={(e) => setVendorForm(prev => ({ ...prev, vendor_name: e.target.value }))}
              />
            </div>
            <div>
              <Label>Vendor Email *</Label>
              <Input 
                type="email"
                value={vendorForm.vendor_email}
                onChange={(e) => setVendorForm(prev => ({ ...prev, vendor_email: e.target.value }))}
              />
            </div>
            <div>
              <Label>Additional Instructions</Label>
              <Textarea 
                value={vendorForm.additional_instructions}
                onChange={(e) => setVendorForm(prev => ({ ...prev, additional_instructions: e.target.value }))}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSendToVendor(false)}>Cancel</Button>
            <Button onClick={handleSendToVendor} disabled={loading} className="bg-blue-600 hover:bg-blue-700">
              <Send className="w-4 h-4 mr-2" />
              {loading ? 'Sending...' : 'Send to Vendor'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Record Response Dialog */}
      <Dialog open={showRecordResponse} onOpenChange={setShowRecordResponse}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5" />
              Record ID Verification
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Verifier Name *</Label>
                <Input 
                  value={responseForm.verifier_name}
                  onChange={(e) => setResponseForm(prev => ({ ...prev, verifier_name: e.target.value }))}
                />
              </div>
              <div>
                <Label>Verification Date *</Label>
                <Input 
                  type="date"
                  value={responseForm.verification_date}
                  onChange={(e) => setResponseForm(prev => ({ ...prev, verification_date: e.target.value }))}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>PAN Verified</Label>
                <Select 
                  value={responseForm.pan_verified}
                  onValueChange={(v) => setResponseForm(prev => ({ ...prev, pan_verified: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yes">Yes - PAN Verified</SelectItem>
                    <SelectItem value="no">No - Unable to Verify</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>PAN Status</Label>
                <Select 
                  value={responseForm.pan_status}
                  onValueChange={(v) => setResponseForm(prev => ({ ...prev, pan_status: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                    <SelectItem value="not_found">Not Found</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Name on PAN</Label>
              <Input 
                value={responseForm.name_on_pan}
                onChange={(e) => setResponseForm(prev => ({ ...prev, name_on_pan: e.target.value }))}
                placeholder="Name as appearing on PAN card"
              />
            </div>

            <div>
              <Label>Name Match Status</Label>
              <Select 
                value={responseForm.pan_match_status}
                onValueChange={(v) => setResponseForm(prev => ({ ...prev, pan_match_status: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="matched">Matched - Name matches exactly</SelectItem>
                  <SelectItem value="partial_match">Partial Match - Minor variations</SelectItem>
                  <SelectItem value="mismatch">Mismatch - Names do not match</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Overall Status</Label>
              <RadioGroup 
                value={responseForm.overall_status}
                onValueChange={(v) => setResponseForm(prev => ({ ...prev, overall_status: v }))}
                className="flex gap-4 mt-2"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="verified" id="verified" />
                  <Label htmlFor="verified" className="text-emerald-700 cursor-pointer">Verified Correct</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="not_verified" id="not-verified" />
                  <Label htmlFor="not-verified" className="text-amber-700 cursor-pointer">Not Verified</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="discrepancy" id="discrepancy" />
                  <Label htmlFor="discrepancy" className="text-rose-700 cursor-pointer">Discrepancy</Label>
                </div>
              </RadioGroup>
            </div>

            <div>
              <Label>Agent Remarks *</Label>
              <Textarea 
                value={responseForm.agent_remarks}
                onChange={(e) => setResponseForm(prev => ({ ...prev, agent_remarks: e.target.value }))}
                placeholder="Enter verification remarks..."
                rows={3}
              />
            </div>

            <div>
              <Label>Overall Remarks</Label>
              <Textarea 
                value={responseForm.overall_remarks}
                onChange={(e) => setResponseForm(prev => ({ ...prev, overall_remarks: e.target.value }))}
                placeholder="Summary of ID verification..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRecordResponse(false)}>Cancel</Button>
            <Button onClick={handleRecordResponse} disabled={loading} className="bg-emerald-600 hover:bg-emerald-700">
              <CheckCircle className="w-4 h-4 mr-2" />
              {loading ? 'Saving...' : 'Save Verification'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Annexure Dialog */}
      <Dialog open={showUploadAnnexure} onOpenChange={setShowUploadAnnexure}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-blue-600" />
              Upload Document
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Document Type *</Label>
              <Select 
                value={annexureForm.annexure_type}
                onValueChange={(v) => setAnnexureForm(prev => ({ ...prev, annexure_type: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pan_card">PAN Card</SelectItem>
                  <SelectItem value="pan_verification_report">PAN Verification Report</SelectItem>
                  <SelectItem value="aadhaar_card">Aadhaar Card</SelectItem>
                  <SelectItem value="other_id">Other ID Proof</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>File Name</Label>
              <Input 
                value={annexureForm.file_name}
                onChange={(e) => setAnnexureForm(prev => ({ ...prev, file_name: e.target.value }))}
              />
            </div>
            <div>
              <Label>File URL *</Label>
              <Input 
                value={annexureForm.file_url}
                onChange={(e) => setAnnexureForm(prev => ({ ...prev, file_url: e.target.value }))}
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea 
                value={annexureForm.description}
                onChange={(e) => setAnnexureForm(prev => ({ ...prev, description: e.target.value }))}
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadAnnexure(false)}>Cancel</Button>
            <Button 
              onClick={handleUploadAnnexure} 
              disabled={uploadingAnnexure || !annexureForm.file_url} 
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Upload className="w-4 h-4 mr-2" />
              {uploadingAnnexure ? 'Uploading...' : 'Upload'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
